import { useEffect, useMemo, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { tightenRichTextSpacing } from '@/lib/tighten-rich-text-spacing';
import './rich-text-editor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  /** Optional test id on the editor shell (Document Language, etc.). */
  testId?: string;
  'aria-label'?: string;
}

/**
 * Same Jodit toolbar used for Settings → document templates — the
 * formatting layout that appears most often in LarkPM. Do not replace
 * this with a custom icon row.
 */
export const STANDARD_JODIT_BUTTONS = [
  'bold',
  'italic',
  'underline',
  '|',
  'font',
  'fontsize',
  '|',
  'brush',
  'paragraph',
  '|',
  'outdent',
  'indent',
  '|',
  'ul',
  'ol',
  '|',
  'image',
  'link',
  'table',
  '|',
  'align',
  'undo',
  'redo',
  '|',
  'hr',
  'eraser',
  'copyformat',
] as const;

/** One-click list toggle — no style submenu (that popup was a no-op under Settings overflow). */
function execListCommand(
  jodit: {
    focus?: () => void;
    execCommand: (command: string, showUI?: boolean, value?: string | null) => void;
  },
  command: string,
) {
  jodit.focus?.();
  jodit.execCommand(command, false, null);
}

export function RichTextEditor({
  value,
  onChange,
  readOnly,
  placeholder,
  testId = 'rich-text-editor',
  'aria-label': ariaLabel,
}: RichTextEditorProps) {
  const emittedRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const focusedRef = useRef(false);
  const [seed, setSeed] = useState(value);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Only re-seed from props when the editor is idle. Pushing `value` while
  // focused races toolbar commands (ol/ul/bold) and can wipe the apply.
  useEffect(() => {
    if (value === emittedRef.current) return;
    if (focusedRef.current) {
      emittedRef.current = value;
      return;
    }
    emittedRef.current = value;
    setSeed(value);
  }, [value]);

  const config = useMemo(
    () => ({
      readonly: !!readOnly,
      placeholder: placeholder ?? '',
      height: 220,
      minHeight: 160,
      toolbar: true,
      toolbarButtonSize: 'small' as const,
      theme: 'default',
      spellcheck: true,
      language: 'en',
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      processPasteHTML: true,
      processPasteFromWord: true,
      removeButtons: ['fullsize', 'about'],
      buttons: [...STANDARD_JODIT_BUTTONS],
      buttonsXS: [
        'bold',
        'italic',
        'underline',
        '|',
        'paragraph',
        '|',
        'ul',
        'ol',
        '|',
        'link',
        '|',
        'undo',
        'redo',
      ],
      // Override default ol/ul controls that open a list-style popup first.
      // Under Document Language cards that popup is often clipped — click
      // looked dead. Apply the list immediately instead.
      controls: {
        ol: {
          command: 'insertOrderedList',
          tags: ['ol' as const],
          tooltip: 'Insert Ordered List',
          exec: (jodit: Parameters<typeof execListCommand>[0]) =>
            execListCommand(jodit, 'insertOrderedList'),
        },
        ul: {
          command: 'insertUnorderedList',
          tags: ['ul' as const],
          tooltip: 'Insert Unordered List',
          exec: (jodit: Parameters<typeof execListCommand>[0]) =>
            execListCommand(jodit, 'insertUnorderedList'),
        },
      },
      events: {
        focus: () => {
          focusedRef.current = true;
        },
        blur: () => {
          focusedRef.current = false;
        },
      },
    }),
    [readOnly, placeholder],
  );

  const publish = (html: string) => {
    const next = tightenRichTextSpacing(html);
    emittedRef.current = next;
    onChangeRef.current(next);
  };

  if (readOnly) {
    return (
      <div
        className="prose prose-sm max-w-none font-public-sans text-foreground"
        data-testid={testId}
        aria-label={ariaLabel}
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(
            value || `<p class="italic text-muted-foreground">${placeholder ?? ''}</p>`,
          ),
        }}
      />
    );
  }

  return (
    <div
      className="rich-text-editor-jodit"
      data-testid={testId}
      aria-label={ariaLabel}
    >
      <JoditEditor
        value={seed}
        config={config}
        onBlur={publish}
        onChange={publish}
      />
    </div>
  );
}
